import { Link } from "react-router-dom";
import { ArrowRight, Eye, Shield, Target, Clock, Users, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import Layout from "@/components/layout/Layout";
import PageHeader from "@/components/layout/PageHeader";
import Seo from "@/components/Seo";

const AboutPage = () => {
  return (
    <Layout>
      <Seo
        title="За нас"
        path="/za-nas"
        description="Запознайте се с Outsource Point — българска аутсорсинг компания, основана през 2016 г., специализирана в кол център услуги и комуникационни решения."
      />
      <PageHeader 
        subtitle="За компанията"
        title="За нас"
        description="Научете повече за историята, мисията и ценностите на Outsource Point"
      />

      {/* Company Story */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="space-y-8">
            {/* Main intro with image */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              <div className="space-y-6">
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
                  Повече от бизнес
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed">
                  <span className="font-semibold text-[#2E4A91]">
                    „Аутсорс Пойнт Груп" ЕООД
                  </span>{" "}
                  е Българска аутсорсинг компания, специализирана в предоставянето Кол-център услуги, както и други телекомуникационни решения в работата с крайни клиенти и клиентско обслужване. Компанията ни се отличава с това, че може да предостави пълен спектър от решения, свързани с ежедневната работа с крайни клиенти.
                </p>
                <p className="text-lg text-slate-600 leading-relaxed">
                  Основана през 2016г, компанията стартира своята Кол-център дейност, тясно специализирайки се в изходящите и входящи кампании за клиентско обслужване.
                </p>
              </div>

              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=800&q=80"
                  alt="Екип на Outsource Point"
                  className="rounded-2xl shadow-xl"
                />
                <div className="absolute -bottom-6 -left-6 bg-[#2E4A91] text-white p-6 rounded-xl shadow-lg">
                  <div className="text-4xl font-bold">2016</div>
                  <div className="text-sm opacity-80">Година на основаване</div>
                </div>
              </div>
            </div>

            {/* Full width text continuation */}
            <div className="max-w-5xl mx-auto space-y-6 pt-8">
              <p className="text-lg text-slate-600 leading-relaxed">
                Аутсорс Пойнт сега има възможността и експертизата да предоставя пълен спектър от комуникационни решения в работата с крайни клиенти и ежедневната обработката на големи клиентски бази – поддръжка на телефонни линии (входящи & изходящи кампании), управление на чат канали Instagram, Viber, Webchat, Messenger, Whatsapp
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                По този начин компанията ни се стреми към своята цел и мисия, която е да предоставя аутсорсинг услуги и комуникационни решения, които да носят добавена стойност на нашите партньори и техните клиенти.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-[#2E4A91]">8+</div>
              <p className="text-slate-600 mt-2">Години опит</p>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-[#2ED573]">50+</div>
              <p className="text-slate-600 mt-2">Служители</p>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-[#2E4A91]">100+</div>
              <p className="text-slate-600 mt-2">Успешни проекти</p>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-[#2ED573]">24/7</div>
              <p className="text-slate-600 mt-2">Поддръжка</p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
              Мисия и визия
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="bg-[#2E4A91] text-white p-8 lg:p-12 rounded-2xl">
              <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center mb-6">
                <Target className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Нашата мисия</h3>
              <p className="text-white/90 text-lg leading-relaxed">
                Да предоставяме аутсорсинг услуги и комуникационни решения, които
                добавят стойност за нашите партньори и техните клиенти. Стремим се
                да бъдем надежден партньор, който разбира нуждите на бизнеса и
                предлага ефективни решения.
              </p>
            </div>

            <div className="bg-slate-50 p-8 lg:p-12 rounded-2xl">
              <div className="w-14 h-14 bg-[#2ED573]/10 rounded-xl flex items-center justify-center mb-6">
                <Award className="w-7 h-7 text-[#2ED573]" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">
                Нашата визия
              </h3>
              <p className="text-slate-600 text-lg leading-relaxed">
                Да станем водещ доставчик на кол център и телекомуникационни
                услуги в България, известен с високото качество, иновациите и
                отдадеността към успеха на нашите клиенти.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="section-padding bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold text-[#2ED573] uppercase tracking-wider">
              Нашите ценности
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-3 mb-4">
              3 неща, които ни отличават
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Държим изключително много на 3 неща, които ни отличават от другите в бранша ни
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100 card-hover">
              <div className="w-16 h-16 mb-6 bg-[#2E4A91]/10 rounded-full flex items-center justify-center">
                <Eye className="w-8 h-8 text-[#2E4A91]" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-[#2E4A91]">
                Прозрачност
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Работим изключително прозрачно. Всички наши партньори имат достъпи до системата ни и могат постоянно да видят какво се случва с всички наши агенти, ангажирани с техни кампании. Подробно може да се получават дневни анализи на разговорите с избрани KPI показатели, както и да се прослушват всички разговори до 12м назад.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100 card-hover">
              <div className="w-16 h-16 mb-6 bg-[#2ED573]/10 rounded-full flex items-center justify-center">
                <Shield className="w-8 h-8 text-[#2ED573]" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-[#2E4A91]">
                Коректност
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Компанията ни държи изключително много на коректното отношение във всеки един аспект на бизнеса. Няма да Ви предложим решение, което не смятаме, че е подходящо за Вас. Няма да Ви предложим повече агенти за Вашата кампания ако такива не са Ви нужни. Няма и да Ви заложим скрити такси или комисионни. Ние държим изключително много на дългосрочните партньорства, а такива не могат да бъдат изградени без коректно отношение!
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100 card-hover">
              <div className="w-16 h-16 mb-6 bg-[#2E4A91]/10 rounded-full flex items-center justify-center">
                <Target className="w-8 h-8 text-[#2E4A91]" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-[#2E4A91]">
                Отговорност
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Подхождаме отговорно към всеки един проблем или поставена задача. Доброто изпълнение идва в следствие на добрата подготовка, която няма как се направи без отговорно отношение към заложената задача. Винаги се стремим да подобряваме нашето представяне и да покриваме цифрите, които заедно таргетираме с нашите партньори.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Technology */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-sm font-semibold text-[#2ED573] uppercase tracking-wider">
                Технологии
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-3 mb-6">
                Модерни дигитални решения
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                Използваме инструменти от следващо поколение и автоматизирани
                решения за продажби. Нашите системи са достъпни 24/7 със 100%
                сигурност на данните.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#2ED573]/10 flex items-center justify-center flex-shrink-0 mt-1">
                    <Clock className="w-4 h-4 text-[#2ED573]" />
                  </div>
                  <span className="text-slate-600">
                    Системи с достъпност 24/7 без прекъсвания
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#2ED573]/10 flex items-center justify-center flex-shrink-0 mt-1">
                    <Shield className="w-4 h-4 text-[#2ED573]" />
                  </div>
                  <span className="text-slate-600">
                    100% сигурност на данните по GDPR стандарти
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#2ED573]/10 flex items-center justify-center flex-shrink-0 mt-1">
                    <Users className="w-4 h-4 text-[#2ED573]" />
                  </div>
                  <span className="text-slate-600">
                    Пълен достъп за партньори до мониторинг системата
                  </span>
                </li>
              </ul>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=800&q=80"
                alt="Технологии"
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-r from-[#2E4A91] to-[#243b75] py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Искате да научите повече?
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Запознайте се с нашия екип от професионалисти
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/ekip">
              <Button className="bg-white text-[#2E4A91] hover:bg-slate-100 text-lg px-8 h-14 rounded-lg font-semibold">
                Вижте екипа
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/uslugi">
              <Button
                variant="outline"
                className="border-white text-white hover:bg-white/10 text-lg px-8 h-14 rounded-lg font-semibold"
              >
                Нашите услуги
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default AboutPage;
